import { useState, useEffect, useCallback } from 'react'
import { Link, useSearchParams } from 'react-router'
import {
  Box,
  Stack,
  Tab,
  Tabs,
  Typography,
  Alert,
  Button,
  CircularProgress,
  Checkbox,
  FormControlLabel
} from '@mui/material'
import { useSessionStore } from '../state'
import { useDiveStore } from '../stores/diveStore'
import { queryNearbyDives, getHostedDives } from '../services/diveService'
import {
  getUserRequests,
  UserJoinRequestWithDive
} from '../services/joinRequestService'
import DiveCard from '../components/DiveCard'
import LocationRefreshButton from '../components/LocationRefreshButton'
import AddressAutocomplete from '../components/AddressAutocomplete'
import routes from './routes'
import Database from '../types/Database'

type TabValue = 'dives' | 'pending' | 'scheduled'

const Discovery = () => {
  const profile = useSessionStore(s => s.profile)
  const user = useSessionStore(s => s.user)
  const dives = useDiveStore(s => s.dives)
  const isLoading = useDiveStore(s => s.isLoading)
  const error = useDiveStore(s => s.error)
  const setDives = useDiveStore(s => s.setDives)
  const setLoading = useDiveStore(s => s.setLoading)
  const setError = useDiveStore(s => s.setError)
  const setReferenceLocation = useDiveStore(s => s.setReferenceLocation)

  const [searchParams] = useSearchParams()
  const initialTab = (['dives', 'pending', 'scheduled'] as TabValue[]).includes(
    searchParams.get('tab') as TabValue
  )
    ? (searchParams.get('tab') as TabValue)
    : 'dives'
  const [tab, setTab] = useState<TabValue>(initialTab)
  const [searchLocation, setSearchLocation] = useState<{
    lat: number
    lng: number
    displayName: string
  } | null>(null)
  const [requestedDives, setRequestedDives] = useState<
    UserJoinRequestWithDive[]
  >([])
  const [approvedDives, setApprovedDives] = useState<UserJoinRequestWithDive[]>(
    []
  )
  const [ownFutureDives, setOwnFutureDives] = useState<Database.Dive[]>([])
  const [requestedLoading, setRequestedLoading] = useState(false)
  const [scheduledLoading, setScheduledLoading] = useState(false)
  const [showPastDives, setShowPastDives] = useState(false)

  const fetchDives = useCallback(
    async (center: { lat: number; lng: number }) => {
      if (!user) return

      setLoading(true)
      setError(null)

      try {
        const results = await queryNearbyDives(center, 100, user.uid)
        setDives(results)
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to load nearby dives'
        setError(message)
      } finally {
        setLoading(false)
      }
    },
    [user, setDives, setLoading, setError]
  )

  const fetchRequested = useCallback(async () => {
    setRequestedLoading(true)
    try {
      const results = await getUserRequests('pending')
      setRequestedDives(results)
    } catch {
      // Silently fail — user sees empty list
    } finally {
      setRequestedLoading(false)
    }
  }, [])

  const fetchScheduled = useCallback(async () => {
    if (!user) return
    setScheduledLoading(true)
    try {
      const [approved, hosted] = await Promise.all([
        getUserRequests('approved'),
        getHostedDives(user.uid)
      ])
      setApprovedDives(approved)
      const now = new Date()
      const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)

      if (showPastDives) {
        // Filter approved dives to include past ones from up to 1 month ago
        const filteredApproved = approved.filter(
          ({ dive }) => dive.dateTime.toDate() > oneMonthAgo
        )
        setApprovedDives(filteredApproved)
        // Show past dives from up to 1 month ago
        const pastAndFutureDives = hosted.filter(
          dive => dive.dateTime.toDate() > oneMonthAgo
        )
        setOwnFutureDives(pastAndFutureDives)
      } else {
        // Only future approved dives
        const futureApproved = approved.filter(
          ({ dive }) => dive.dateTime.toDate() > now
        )
        setApprovedDives(futureApproved)
        // Only future, non-cancelled dives
        const futureDives = hosted.filter(
          dive =>
            dive.status !== 'cancelled' &&
            dive.status !== 'completed' &&
            dive.dateTime.toDate() > now
        )
        setOwnFutureDives(futureDives)
      }
    } catch {
      // Silently fail — user sees empty list
    } finally {
      setScheduledLoading(false)
    }
  }, [user, showPastDives])

  useEffect(() => {
    if (!profile?.location) return

    const center = {
      lat: profile.location.latitude,
      lng: profile.location.longitude
    }
    setReferenceLocation(center)
    fetchDives(center)
  }, [profile, fetchDives, setReferenceLocation])

  useEffect(() => {
    if (tab === 'pending') fetchRequested()
    if (tab === 'scheduled') fetchScheduled()
  }, [tab, fetchRequested, fetchScheduled, showPastDives])

  const handleLocationRefresh = (location: { lat: number; lng: number }) => {
    fetchDives(location)
  }

  const handleRetry = () => {
    const referenceLocation = useDiveStore.getState().referenceLocation
    if (referenceLocation) {
      fetchDives(referenceLocation)
    }
  }

  const renderDives = () => {
    if (isLoading && dives.length === 0) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '30vh'
          }}
        >
          <CircularProgress />
        </Box>
      )
    }

    return (
      <Stack spacing={2}>
        {error && (
          <Alert
            severity='error'
            action={
              <Button color='inherit' size='small' onClick={handleRetry}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {!error && dives.length === 0 && !isLoading && (
          <Typography
            variant='body1'
            color='text.secondary'
            sx={{ textAlign: 'center', mt: 4 }}
          >
            No nearby dives available
          </Typography>
        )}

        {dives.map(dive => (
          <Link
            key={dive.id}
            to={routes.diveDetail.replace(':id', dive.id)}
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            <DiveCard dive={dive} />
          </Link>
        ))}
      </Stack>
    )
  }

  const renderPending = () => {
    if (requestedLoading) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '30vh'
          }}
        >
          <CircularProgress />
        </Box>
      )
    }

    if (requestedDives.length === 0) {
      return (
        <Typography
          variant='body1'
          color='text.secondary'
          sx={{ textAlign: 'center', mt: 4 }}
        >
          No pending requests
        </Typography>
      )
    }

    return (
      <Stack spacing={2}>
        {requestedDives.map(({ request, dive }) => (
          <Link
            key={request.id}
            to={routes.diveDetail.replace(':id', dive.id)}
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            <DiveCard dive={dive} />
          </Link>
        ))}
      </Stack>
    )
  }

  const renderScheduled = () => {
    if (scheduledLoading) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '30vh'
          }}
        >
          <CircularProgress />
        </Box>
      )
    }

    const hasApproved = approvedDives.length > 0
    const hasOwnDives = ownFutureDives.length > 0

    return (
      <>
        <FormControlLabel
          control={
            <Checkbox
              checked={showPastDives}
              onChange={e => setShowPastDives(e.target.checked)}
              size='small'
            />
          }
          label={
            <Typography variant='body2'>
              Show past dives (up to 1 month)
            </Typography>
          }
          sx={{ mb: 1 }}
        />

        {!hasApproved && !hasOwnDives ? (
          <Typography
            variant='body1'
            color='text.secondary'
            sx={{ textAlign: 'center', mt: 4 }}
          >
            No scheduled dives
          </Typography>
        ) : (
          <Stack spacing={2}>
            {approvedDives.map(({ request, dive }) => (
              <Link
                key={request.id}
                to={routes.diveDetail.replace(':id', dive.id)}
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                <DiveCard
                  dive={dive}
                  paymentStatus={request.paymentStatus}
                  showDiveStatus
                />
              </Link>
            ))}
            {ownFutureDives.map(dive => (
              <Link
                key={dive.id}
                to={routes.diveDetail.replace(':id', dive.id)}
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                <DiveCard dive={dive} showDiveStatus />
              </Link>
            ))}
          </Stack>
        )}
      </>
    )
  }

  return (
    <>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant='fullWidth'>
          <Tab label='Dives' value='dives' />
          <Tab label='Pending' value='pending' />
          <Tab label='Scheduled' value='scheduled' />
        </Tabs>
      </Box>
      <Box sx={{ p: 2 }}>
        {tab === 'dives' && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 2
            }}
          >
            <Box sx={{ flex: 1 }}>
              <AddressAutocomplete
                value={searchLocation}
                onChange={place => {
                  setSearchLocation(place)
                  if (place) {
                    setReferenceLocation({ lat: place.lat, lng: place.lng })
                    fetchDives({ lat: place.lat, lng: place.lng })
                  }
                }}
              />
            </Box>
            <LocationRefreshButton onLocationRefresh={handleLocationRefresh} />
          </Box>
        )}
        {tab === 'dives' && renderDives()}
        {tab === 'pending' && renderPending()}
        {tab === 'scheduled' && renderScheduled()}
      </Box>
    </>
  )
}

export default Discovery
